import React, { useCallback, useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { FaFacebookF, FaInstagram, FaLinkedinIn, FaGoogle } from "react-icons/fa";
import { FaYoutube } from "react-icons/fa6";
import { BsTwitterX } from "react-icons/bs";

import "../styles/ReviewPrompt.css";
import { trackAnalyticsEvent } from "../utils/analytics";
import { GOOGLE_REVIEW_URL } from "../utils/reviewPrompt";

const socialLinks = [
  {
    label: "Facebook",
    href: "https://www.facebook.com/profile.php?id=100085145831452",
    Icon: FaFacebookF,
  },
  {
    label: "X",
    href: "https://x.com/ImpexKrris93903?t=WvTuF8TlR-cG4TCIN3Wuuw&s=09",
    Icon: BsTwitterX,
  },
  {
    label: "Instagram",
    href: "https://www.instagram.com/krrish.impex/",
    Icon: FaInstagram,
  },
  {
    label: "LinkedIn",
    href: "https://www.linkedin.com/company/krrish-ecoware-industries-pvt-ltd/",
    Icon: FaLinkedinIn,
  },
  {
    label: "Google",
    href: "https://share.google/kOm6nl3ZTc5JsiPtg",
    Icon: FaGoogle,
  },
  {
    label: "YouTube",
    href: "https://www.youtube.com/@Krrish_impex007",
    Icon: FaYoutube,
  },
];

const KEYS = {
  completed: "reviewCompleted",
  dismissed: "reviewDismissed",
  dismissedCount: "reviewDismissedCount",
  dismissedUntil: "reviewDismissedUntil",
  pageVisits: "reviewPageVisits",
  lastPath: "reviewLastVisitedPath",
};

const THIRTY_DAYS = 30 * 24 * 60 * 60 * 1000;
const INITIAL_DELAY = 60 * 1000; // 60 seconds
const PAGES_NEEDED = 3;          // unique pages before re-showing after dismiss

/* ── localStorage helpers ── */
const ls = {
  get: (k) => { try { return localStorage.getItem(k); } catch { return null; } },
  set: (k, v) => { try { localStorage.setItem(k, String(v)); } catch {} },
  remove: (k) => { try { localStorage.removeItem(k); } catch {} },
  num: (k) => {
    const n = Number(localStorage.getItem(k));
    return Number.isFinite(n) ? n : 0;
  },
};

/* ── Eligibility ── */
const canShow = () => {
  if (ls.get(KEYS.completed) === "true") return false;

  const until = ls.num(KEYS.dismissedUntil);
  if (until) {
    if (Date.now() < until) return false;
    // cooldown expired — reset everything
    [KEYS.dismissedUntil, KEYS.dismissed, KEYS.dismissedCount, KEYS.pageVisits, KEYS.lastPath]
      .forEach(ls.remove);
  }
  return true;
};

/* ================================================================
   Component
================================================================ */
const ReviewPrompt = () => {
  const location = useLocation();
  const [open, setOpen] = useState(false);

  // Stable ref so callbacks always see the latest value without re-registering listeners
  const openRef = useRef(open);
  useEffect(() => { openRef.current = open; }, [open]);

  const isAdmin = location.pathname.startsWith("/admin");

  /* ── try to open ── */
  const tryOpen = useCallback(() => {
    if (openRef.current) return; // already visible
    if (!canShow()) return;
    setOpen(true);
  }, []);

  /* ── dismiss ──
     Guard with openRef so tracking only fires when the popup was
     actually visible. Without this, ESC / overlay-click on a page
     where the popup was never open would still fire review_dismissed.
  */
  const handleDismiss = useCallback(() => {
    if (!openRef.current) return; // popup isn't open — nothing to dismiss
    trackAnalyticsEvent("review_dismissed");

    const count = ls.num(KEYS.dismissedCount) + 1;
    ls.set(KEYS.dismissed, "true");
    ls.set(KEYS.dismissedCount, count);
    ls.set(KEYS.pageVisits, "0");
    ls.remove(KEYS.lastPath);

    if (count >= 2) {
      ls.set(KEYS.dismissedUntil, Date.now() + THIRTY_DAYS);
    }
    setOpen(false);
  }, []);

  /* ── confirm review ── */
  const handleReview = useCallback(() => {
    if (!openRef.current) return; // guard for consistency
    trackAnalyticsEvent("review_submitted");
    ls.set(KEYS.completed, "true");
    [KEYS.dismissed, KEYS.dismissedCount, KEYS.dismissedUntil, KEYS.pageVisits, KEYS.lastPath]
      .forEach(ls.remove);
    setOpen(false);
    window.open(GOOGLE_REVIEW_URL, "_blank", "noopener,noreferrer");
  }, []);

  /* ── TRIGGER 1 — 60-second timer ── */
  useEffect(() => {
    if (isAdmin) return;
    const id = setTimeout(tryOpen, INITIAL_DELAY);
    return () => clearTimeout(id);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  /* ── TRIGGER 2 — page-visit counter ── */
  useEffect(() => {
    if (isAdmin || !canShow()) return;

    const currentPath = `${location.pathname}${location.search}`;
    if (ls.get(KEYS.lastPath) === currentPath) return; // same page, skip

    ls.set(KEYS.lastPath, currentPath);

    const visits = ls.num(KEYS.pageVisits) + 1;
    ls.set(KEYS.pageVisits, visits);

    if (visits >= PAGES_NEEDED) {
      tryOpen();
    }
  }, [location.pathname, location.search]); // eslint-disable-line react-hooks/exhaustive-deps

  /* ── TRIGGER 3 — intent event (fired after form submission) ── */
  useEffect(() => {
    if (isAdmin) return;

    const onIntent = () => {
      if (ls.get(KEYS.completed) === "true") return;
      const until = ls.num(KEYS.dismissedUntil);
      if (until && Date.now() < until) return;
      tryOpen();
    };

    window.addEventListener("review-prompt:intent", onIntent);
    return () => window.removeEventListener("review-prompt:intent", onIntent);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  /* ── ESC key ── */
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === "Escape") handleDismiss(); };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open, handleDismiss]);

  if (!open) return null;

  return (
    <div
      className="review-prompt-overlay"
      role="presentation"
      onClick={handleDismiss}
    >
      <div
        className="review-prompt"
        role="dialog"
        aria-modal="true"
        aria-labelledby="review-prompt-title"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          type="button"
          className="review-prompt-close"
          aria-label="Dismiss review prompt"
          onClick={handleDismiss}
        >
          &times;
        </button>
        <p className="review-prompt-kicker">Krrish Ecoware Industries PVT. LTD.</p>

        <div className="review-prompt-stars">⭐⭐⭐⭐⭐</div>

        <h2 id="review-prompt-title">Rate Your Experience</h2>

        <p className="review-prompt-copy">
          If you're satisfied with our products and service, we'd appreciate a
          5-star Google review. Your feedback helps more businesses discover our
          disposable household cutlery, tableware, and packaging solutions.
        </p>
        <div className="review-prompt-actions">
          <button type="button" className="review-prompt-primary" onClick={handleReview}>
            Leave a review
          </button>
          <button type="button" className="review-prompt-secondary" onClick={handleDismiss}>
            Dismiss
          </button>
        </div>

        <div className="review-prompt-social">
          <p>Also connect with us</p>
          <div className="review-prompt-social-icons" aria-label="Social media links">
            {socialLinks.map(({ label, href, Icon }) => (
              <a
                key={label}
                href={href}
                target="_blank"
                rel="noopener noreferrer"
                aria-label={label}
                title={label}
              >
                <Icon aria-hidden="true" />
              </a>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ReviewPrompt;
